import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import clsx from 'clsx';
import styles from './AddVcForm.module.css';
import { Modal , Button , Tooltip} from '@material-ui/core';
import { reasons, agencies } from '../../staticData';
import { API } from '../../apis/api';
import {
  API_RESOURCE_URLS,
  DATE_FORMAT,
  DISPLAY_MESSAGES,
  MESSAGE_TYPE,
  ADD_COLOUR_OR_COUNTRY
} from '../../constants';
import {
  convertToTitleCase,
  isPositiveInteger,
  isQuantityValid,
  isEvrNumberValid,
} from '../../utils/utils';
import './AddVcForm.module.css';
import { getSequenceStartDateString } from '../../utils/tableUtils';
import { usePopupManager } from '../../providers/PopupManager/PopupManager';
import {
  CustomDatePicker,
  CustomSelect,
  EllipsedText,
  ValidatingTextField,
} from '../FormComponents';
import { useErrorHandler } from 'react-error-boundary';
import { buildErrorMessage } from '../../apis/calls';

import AddCircleIcon from '@material-ui/icons/AddCircle';
import AddCountryOrColourForm from '../AddCountryOrColourForm/AddCountryOrColourForm';

const AddVcForm = ({
  handleClose,
  lastAsnGeneratedDate,
  open,
  updatePlanRows,
  vcPlanData,
  skipToLastPage,
  plant,
  plantName,
  line,
  lineName,
}) => {

  const [showCountryPopup, setShowCountryPopup] = useState(false);
  const [showColourPopup, setShowColourPopup] = useState(false);


  const { showPopup } = usePopupManager();
  const handleError = useErrorHandler();

  const YES = 'yes';
  const NO = 'no';

  const formContainerRef = useRef(null);

  const [vc, setVc] = useState('');
  const [market, setMarket] = useState('');
  const [category, setCategory] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [defence, setDefence] = useState('');
  const [fbv, setFbv] = useState('');
  const [vendorName, setVendorName] = useState('');
  const [dayTarget, setDayTarget] = useState('');
  const [evrNumber, setEvrNumber] = useState('');
  const [startDate, setStartDate] = useState('');
  const [reason, setReason] = useState('');
  const [agency, setAgency] = useState('');
  const [comment, setComment] = useState('');
  const [buildCardNumber, setBuildCardNumber] = useState(null);
  const [powerSteering, setPowerSteering] = useState(null);
  const [colour, setColour] = useState('');
  const [
    displayVcCombinationNotUniqueError,
    setDisplayVcCombinationNotUniqueError,
  ] = useState(false);
  const [highlightMandatoryFields, setHighlightMandatoryFields] = useState(
    false,
  );

  const [countries, setCountries] = useState({ loading: false, data: [] });
  const [vcs, setVcs] = useState({ loading: false, data: [] });
  const [EvrOptions, setEvrOptions] = useState([])  
  const [fbvVendors, setFbvVendors] = useState({ loading: false, data: [] });
  const [powerSteeringVendors, setPowerSteeringVendors] = useState({
    loading: false,
    data: [],
  });
  const [colours, setColours] = useState({ loading: false, data: [] });
  const [evrNumberBalance, setEvrNumberBalance] = useState(null);

  const existingPlannedVcCombinations = vcPlanData.map(
    ({
      vc,
      category,
      countryCode,
      fbv,
      fbvVendorCode,
      market,
      defence,
      buildCardNumber,
      powerSteering,
      colourCode,
    }) => ({
      vc,
      category,
      countryCode,
      fbv,
      fbvVendorCode,
      market,
      defence,
      buildCardNumber,
      powerSteering,
      colourCode,
    }),
  );

  const formatOptionsForSelect = (...options) =>
    options.map(option => ({ value: option, label: option }));

  const marketOptions = [
    { value: 'E', label: 'Export' },
    { value: 'D', label: 'Domestic' },
  ];

  const categoryOptions = formatOptionsForSelect('MTO', 'MTS');
  const yesAndNoOptions = [
    { value: YES, label: 'Yes' },
    { value: NO, label: 'No' },
  ];

  const reasonOptions = formatOptionsForSelect(...reasons);
  const agencyOptions = formatOptionsForSelect(...agencies);

  const handleSelectionCurry = setState => valueObject => setState(valueObject);

  const getEvrBalance = (evrNumber) => {
    const selectedOption = EvrOptions.find(option => option.value === evrNumber);
    return selectedOption ? selectedOption.balenceQuantity : 0;
  };
  
  const handleSelectionCurryEvr = setState => valueObject => {
    setState(valueObject);
    const balance = getEvrBalance(valueObject.value);
    setEvrNumberBalance(balance);
  };
  
  const loadData = async (setState, url, params) => {
    try {
      const response = await API.get(url, {
        params: params,
      });
      setState({ loading: false, data: response.data });
    } catch (error) {
      console.error(buildErrorMessage(error));
      handleError(error);
    }
  };

  const loadVcs = async () => {
    return loadData(setVcs, API_RESOURCE_URLS.VC_DESCRIPTION, {
      line,
      plant,
    });
  };
  const loadEvrNumber = async () => {
    try {
      const country = countryCode.label.split(" - ")[1];
      const response = await API.get(API_RESOURCE_URLS.getEvrNumberOptions(plant),{
        params: {
          vc:vc.value,
          plannedDate: startDate, 
          countryCode: countryCode.value,
          country: country,
          line:line,
        }
      }); 
      const formattedOptions = response.data.map(({ evrNumber, balenceQuantity }) => ({
        label: evrNumber,
        value: evrNumber, 
        balenceQuantity
      })); 
      setEvrOptions(formattedOptions);
    } catch (error) {
      console.error('Error fetching EVR options:', error);
    }
  };

  const loadCountries = async () => {
    return loadData(setCountries, API_RESOURCE_URLS.COUNTRIES);
  };

  const loadFbvVendors = async () => {
    return loadData(setFbvVendors, API_RESOURCE_URLS.FBV_VENDORS);
  };

  const loadPowerSteeringVendors = async () => {
    return loadData(
      setPowerSteeringVendors,
      API_RESOURCE_URLS.POWER_STEERING_VENDORS,
    );
  };

  const loadColours = async () => {
    return loadData(setColours, API_RESOURCE_URLS.COLOURS);
  };

  const resetState = async () => {
    setVc(null);
    setMarket(null);
    setCategory(null);
    setCountryCode(null);
    setDefence(null);
    setFbv(null);
    setBuildCardNumber(null);
    setPowerSteering(null);
    setVendorName(null);
    setDayTarget(null);
    setEvrNumber(null);
    setStartDate(null);
    setReason(null);
    setAgency(null);
    setComment(null);
    setDisplayVcCombinationNotUniqueError(null);
    setHighlightMandatoryFields(null);
    setEvrNumberBalance(null);
    setColour(null);
  };

  useEffect(() => {
    if (open) {
      loadEvrNumber();
      loadVcs();
      loadCountries();
      loadFbvVendors();
      loadPowerSteeringVendors();
      loadColours();
    } else {
      resetState();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plant, line, showColourPopup, showCountryPopup]);

  const getVcOptions = () =>
    formatOptionsForSelect(...vcs.data.map(({ vc }) => vc));

  const getDescriptionForVc = vcSelection => {
    const selectedVc = vcSelection && vcSelection.value;
    const selectedVcData = vcs.data.filter(({ vc }) => vc === selectedVc)[0];
    return selectedVcData ? selectedVcData.description : <strong>--</strong>;
  };
  const getMajorModelForVc = vcSelection => {
    const selectedVc = vcSelection && vcSelection.value;
    const selectedVcData = vcs.data.filter(({ vc }) => vc === selectedVc)[0];
    return selectedVcData ? selectedVcData.majorModel : <strong>--</strong>;
  };

  const getCountryCodeOptions = () => {
    return countries.data
      .map(({ code, country }) => ({
        value: code,
        label: `${code} - ${country}`,
      }))
      .sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
  };
  

  const getFbvVendorOptions = () => {
    return fbvVendors.data.map(({ name }) => ({
      value: name,
      label: convertToTitleCase(name),
    }));
  };

  const getPowerSteeringOptions = () => {
    return powerSteeringVendors.data.map(({ vendor }) => ({
      value: vendor,
      label: vendor,
    }));
  };

  const getColourOptions = () => {
    return colours.data.map(({ colourCode, colourDescription }) => ({
      value: colourDescription + ' (' + colourCode + ')',
      label: colourDescription + ' (' + colourCode + ')',
    }));
  };

  const isFbv = () => {
    return fbv && fbv.value === YES;
  };

  const isEvr = () => {
    return  market?.value !== 'E';
  }

  const getVendorCode = vendorSelection => {
    const selectedVendorName = vendorSelection && vendorSelection.value;
    const selectedVendorCode = fbvVendors.data.filter(
      ({ name }) => name === selectedVendorName,
    )[0];
    return selectedVendorCode ? selectedVendorCode.code : null;
  };

  const renderVendorCode = vendorSelection => {
    const selectedVendorCode = getVendorCode(vendorSelection);
    return selectedVendorCode || <strong>--</strong>;
  };

  const checkIfVcCombinationAlreadyExists = (
    newVcCombination,
    existingVcCombinations,
  ) => {
    return existingVcCombinations.some(
      vcCombination =>
        vcCombination.category === newVcCombination.category &&
        vcCombination.countryCode === newVcCombination.countryCode &&
        vcCombination.defence === newVcCombination.defence &&
        vcCombination.fbv === newVcCombination.fbv &&
        vcCombination.fbvVendorCode === newVcCombination.fbvVendorCode &&
        vcCombination.market === newVcCombination.market &&
        vcCombination.vc === newVcCombination.vc &&
        vcCombination.buildCardNumber === newVcCombination.buildCardNumber &&
        vcCombination.powerSteering === newVcCombination.powerSteering &&
        vcCombination.colourCode === newVcCombination.colourCode &&
        vcCombination.evrNumber === newVcCombination.evrNumber,
    );
  };

  const validateFields = fields => {
    const {
      fbvVendorCode,
      buildCardNumber,
      powerSteering,
      ...requiredFields
    } = fields;
    const hasMissingRequiredFields = Object.values(requiredFields).some(
      field => !field,
    );
    if (hasMissingRequiredFields) {
      return false;
    }
    if (fbv === YES && !fbvVendorCode) return false;
    const { dayTarget } = fields;
    if (!isPositiveInteger(dayTarget)) return false;
    if (!isQuantityValid(dayTarget)) return false;
    return true;
  };

  const getColourCode = colourCodeWithDescription =>
    colourCodeWithDescription?.value?.match(/\(([^)]+)\)/)[1] || null;

  const getSelectedVcCombination = () => ({
    category: category && category.value,
    countryCode: countryCode && countryCode.value,
    defence: defence && defence.value,
    fbv: fbv && fbv.value,
    fbvVendorCode: getVendorCode(vendorName),
    market: market && market.value,
    vc: vc && vc.value,
    buildCardNumber: buildCardNumber && buildCardNumber,
    powerSteering: powerSteering && powerSteering.value,
    colourCode: getColourCode(colour),
    ...(market?.value === "E" && { evrNumber: evrNumber && evrNumber.value }),
  });

  const scrollFormToTop = () => {
    formContainerRef.current.scrollTop = 0;
  };

  const runPreSubmissionChecks = () => {
    const vcCombination = getSelectedVcCombination();
    const vcCombinationAlreadyExists = checkIfVcCombinationAlreadyExists(
      vcCombination,
      existingPlannedVcCombinations,
    );
    if (vcCombinationAlreadyExists) {
      scrollFormToTop();
      setDisplayVcCombinationNotUniqueError(true);
      return false;
    }

    const areFieldsValid = validateFields({
      ...vcCombination,
      reason,
      agency,
      dayTarget,
      startDate,
    });

    if (!areFieldsValid) {
      setHighlightMandatoryFields(true);
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    const vcCombination = getSelectedVcCombination();
    try {
      await API.put(`${API_RESOURCE_URLS.VC_QUANTITY}`, {
        plant,
        line,
        ...vcCombination,
        quantities: [{ date: startDate, quantity: Number(dayTarget) }],
        reason: reason.value,
        agency: agency.value,
        evrNumber:evrNumber ? evrNumber.value : null,
        comment,
      });
      handleClose();
      showPopup({
        type: MESSAGE_TYPE.SUCCESS,
        contextText: 'VC added.',
        info: `VC no: ${vc.label}`,
      });
      await updatePlanRows();
      skipToLastPage();
    } catch (error) {
      console.error(buildErrorMessage(error));
      showPopup({
        type: MESSAGE_TYPE.FAILURE,
        contextText: DISPLAY_MESSAGES.VC_ADD_FAILURE,
        info: 'Plan Revision failed.',
        error: error,
      });
    }
  };
  useEffect(() => {
    if (vc && startDate && countryCode) {
      loadEvrNumber(vc,startDate, countryCode);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vc, startDate, countryCode]);
  

  return (
    <>
      <Modal open={open} onClose={handleClose} disableBackdropClick>
        <div className={styles.addVc} ref={formContainerRef}>
          <div className={styles.addVcTitle}>
            Plan Revision: Add a VC
            <div className={styles.plantInfo}>
              Line <strong>{lineName}</strong>
            </div>
            <div className={styles.plantInfo}>
              Plant <strong>{plantName}</strong>
            </div>
          </div>
          <div className={styles.frm}>
            <div className={styles.formSec}>
              <span className={styles.optionalFields}>** Optional Fields</span>

              <span className={styles.rowTitleTop}>Basic Info</span>
              <div className={styles.col8}>
                <div className={styles.formRow}>
                  <label className={styles.label}>VC Number</label>
                  <CustomSelect
                    name="vc"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    showError={displayVcCombinationNotUniqueError}
                    options={getVcOptions(vcs.data)}
                    className={clsx(styles.select, styles.sel1)}
                    value={vc}
                    onChange={handleSelectionCurry(setVc)}
                    maxMenuHeight={300}
                  />
                  <span className={clsx(styles.errorText)}>
                    {displayVcCombinationNotUniqueError &&
                      DISPLAY_MESSAGES.DUPLICATE_VC_COMBO_WARNING}
                  </span>
                </div>
              </div>
              <div className={styles.col9}>
                <div className={clsx(styles.formRow, styles.descriptionCol)}>
                  <label className={styles.label}>Description</label>
                  <EllipsedText
                    value={getDescriptionForVc(vc, vcs.data)}
                    className={styles.description}
                  />
                </div>
              </div>
               {plantName === 'Pantnagar' && (
                <div className={styles.col9}>
                <div className={clsx(styles.formRow, styles.descriptionCol)}>
                  <label className={styles.label}>Major Model</label>
                  <EllipsedText
                    value={getMajorModelForVc(vc, vcs.data)}
                    className={styles.description}
                  />
                </div>
              </div>)}
            </div>
            <div className={clsx(styles.formSec, styles.vcDetail)}>
              <span className={styles.rowTitle}>VC Details</span>
              <div className={styles.col2}>
                <div className={styles.formRow}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Market</label>
                    <CustomSelect
                      name="market"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={marketOptions}
                      className={styles.select}
                      value={market}
                      onChange={handleSelectionCurry(setMarket)}
                    />
                  </div>
                  <div className={styles.col5}>
                    <label className={styles.label}>FBV</label>
                    <CustomSelect
                      name="fbv"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={yesAndNoOptions}
                      className={styles.select}
                      value={fbv}
                      onChange={selection => {
                        if (selection.value === NO) setVendorName(null);
                        handleSelectionCurry(setFbv)(selection);
                      }}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={styles.col4}>
                    <label className={styles.label}>
                      Country
                      <Tooltip title="Add a new country" arrow>
                        <Button
                          startIcon={<AddCircleIcon />}
                          className={clsx(styles.addButton)}
                          onClick={() => setShowCountryPopup(true)}
                          data-testid='add-country-button'
                        >
                        </Button>
                      </Tooltip>

                      <AddCountryOrColourForm
                        open={showCountryPopup}
                        handleClose={() => setShowCountryPopup(false)}
                        formType={ADD_COLOUR_OR_COUNTRY.COUNTRY}
                      />
                    </label> 
                    <CustomSelect
                      name="country"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getCountryCodeOptions()}
                      className={styles.select}
                      value={countryCode}
                      onChange={handleSelectionCurry(setCountryCode)}
                    />
                  </div>
                  <div className={styles.col5}>
                    <label className={styles.label}>Vendor Name</label>
                    <CustomSelect
                      name="vendor-name"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getFbvVendorOptions()}
                      className={clsx(styles.select, styles.sel1)}
                      value={vendorName}
                      onChange={handleSelectionCurry(setVendorName)}
                      isDisabled={!isFbv()}
                    />
                  </div>
                </div>
                <div className={clsx(styles.formRow, styles.vCode)}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Category</label>
                    <CustomSelect
                      name="category"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={categoryOptions}
                      className={styles.select}
                      value={category}
                      onChange={handleSelectionCurry(setCategory)}
                    />
                  </div>
                  <div className={styles.col5}>
                    <label className={styles.label}>Vendor Code</label>
                    <span className={styles.description}>
                      {renderVendorCode(vendorName)}
                    </span>
                  </div>
                </div>
                <div className={clsx(styles.formRow, styles.defence)}>
                  <div className={styles.col4}>
                    <label className={styles.label}>Defence</label>
                    <CustomSelect
                      name="defence"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={yesAndNoOptions}
                      className={styles.select}
                      value={defence}
                      onChange={handleSelectionCurry(setDefence)}
                    />
                  </div>
                  <div className={styles.col5}>
                    <label className={styles.label}>
                      P.Steering <sup>**</sup>
                    </label>
                    <CustomSelect
                      name="powerSteering"
                      options={getPowerSteeringOptions()}
                      className={styles.select}
                      value={powerSteering}
                      onChange={handleSelectionCurry(setPowerSteering)}
                    />
                  </div>
                </div>

                <div className={styles.formRow}>
                  <div className={clsx(styles.col4, styles.newLable)}>
                    <label className={clsx(styles.label)}>
                      BC No. <sup>**</sup>
                    </label>
                    <ValidatingTextField
                      variant="outlined"
                      value={buildCardNumber}
                      onChange={e => {
                        setBuildCardNumber(e.target.value);
                      }}
                      isQuantityValid
                      placeholder="(Enter value)"
                      className={styles.newText}
                      inputProps={{
                        className: styles.newTextInput,
                        'data-testid': 'build-card-number-input',
                      }}
                      FormHelperTextProps={{
                        className: styles.helperText,
                      }}
                    />
                  </div>
                  <div className={styles.col5}>
                    <label className={styles.label}>
                      Colour
                      <Tooltip title="Add a new colour" arrow>
                        <Button
                          startIcon={<AddCircleIcon />}
                          className={clsx(styles.addButton)}
                          onClick={() => setShowColourPopup(true)}
                          data-testid='add-colour-button'
                        >
                        </Button>
                      </Tooltip>
                      <AddCountryOrColourForm
                        open={showColourPopup}
                        handleClose={() => setShowColourPopup(false)}
                        formType={ADD_COLOUR_OR_COUNTRY.COLOUR}
                      />
                    </label>                   
                    <CustomSelect
                      name="colour"
                      isMandatory
                      markIfUnselected={highlightMandatoryFields}
                      options={getColourOptions()}
                      className={clsx(styles.select, styles.sel1)}
                      value={colour}
                      onChange={handleSelectionCurry(setColour)}
                    />
                  </div>
                </div>
              </div>
              <div className={styles.col3}>
                <span className={clsx(styles.rowTitle, styles.detail)}>
                  Plan Details
                </span>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Start date
                  </label>
                  <CustomDatePicker
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    autoOk
                    inputVariant="outlined"
                    value={startDate}
                    onChange={date => {
                      setStartDate(date.format(DATE_FORMAT.ISO));
                    }}
                    inputProps={{
                      className: clsx(styles.select, styles.dateInput),
                    }}
                    emptyLabel="dd/mm/yyyy"
                    minDate={
                      process.env.NODE_ENV === 'test'
                        ? moment(1)
                        : getSequenceStartDateString(lastAsnGeneratedDate)
                    }
                    format="DD/MM/yyyy"
                    data-testid="date-picker-div"
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    Day Target
                  </label>
                  <ValidatingTextField
                    isMandatory
                    validationFn={isPositiveInteger}
                    markIfEmpty={highlightMandatoryFields}
                    isQuantityValid={isQuantityValid(dayTarget)} 
                    validationHelperText={
                      DISPLAY_MESSAGES.POSITIVE_NUMBER_VALIDATION_WARNING
                    }
                    quantityValidationText={DISPLAY_MESSAGES.INVALID_INPUT}
                    variant="outlined"
                    value={dayTarget}
                    onChange={e => setDayTarget(e.target.value)}
                    placeholder="(Enter Target)"
                    className={styles.textField}
                    inputProps={{
                      className: styles.textInput,
                      'data-testid': 'target-number-input',
                    }}
                    FormHelperTextProps={{
                      className: styles.helperText,
                    }}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    EVR/DVR No.
                  </label>
                  <CustomSelect
                      name="EVR/DVR"
                      isMandatory
                      isDisabled={isEvr()}
                      markIfUnselected={highlightMandatoryFields}
                      options={EvrOptions}
                      className={styles.select}
                      value={evrNumber}
                      onChange={handleSelectionCurryEvr(setEvrNumber)}
                      data-testid='evr-number-dropdown'
                    />
                </div>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.lb1)}>
                    EVR Balance
                  </label>
                  <ValidatingTextField
                      disabled
                      variant="outlined"
                      value={evrNumberBalance}
                      isQuantityValid
                      className={styles.newText}
                      inputProps={{
                        className: styles.newTextInput,
                        'data-testid': 'evr-balance-input',
                      }}
                      FormHelperTextProps={{
                        className: styles.helperText,
                      }}
                    />
                </div>
              </div>
            </div>
            <div className={clsx(styles.formSec, styles.vcDetail)}>
              <span className={styles.rowTitle}>Revision Details </span>
              <div className={styles.col6}>
                <div className={styles.formRow}>
                  <label className={styles.label}>Reason</label>
                  <CustomSelect
                    name="reason"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={reasonOptions}
                    className={clsx(styles.select, styles.sel1)}
                    value={reason}
                    onChange={handleSelectionCurry(setReason)}
                  />
                </div>
                <div className={styles.formRow}>
                  <label className={styles.label}>Agency</label>
                  <CustomSelect
                    name="agency"
                    isMandatory
                    markIfUnselected={highlightMandatoryFields}
                    options={agencyOptions}
                    className={clsx(styles.select, styles.sel1)}
                    value={agency}
                    onChange={handleSelectionCurry(setAgency)}
                  />
                </div>
              </div>
              <div className={styles.col7}>
                <div className={styles.formRow}>
                  <label className={clsx(styles.label, styles.commentsLabel)}>
                    Comments<br></br> (Optional)
                  </label>
                  <textarea
                    className={styles.comments}
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                  ></textarea>
                </div>
              </div>
            </div>
            <div className={styles.buttons}>
              <button onClick={handleClose} className={styles.cancel}>
                Cancel
              </button>
              <button
                className={styles.submit}
                onClick={e => {
                  if (e.detail > 1) {
                    console.log('A double-click has been registered');
                    return;
                  }
                  return runPreSubmissionChecks() && handleSubmit();
                }}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </>
  );
};

AddVcForm.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  lastAsnGeneratedDate: PropTypes.objectOf(moment),
  updatePlanRows: PropTypes.func.isRequired,
  vcPlanData: PropTypes.arrayOf(
    PropTypes.shape({
      vc: PropTypes.string,
      category: PropTypes.string,
      countryCode: PropTypes.string,
      fbv: PropTypes.string,
      fbvVendorCode: PropTypes.string,
      market: PropTypes.string,
      defence: PropTypes.string,
    }),
  ),
  skipToLastPage: PropTypes.func.isRequired,
  plant: PropTypes.string.isRequired,
  plantName: PropTypes.string.isRequired,
  line: PropTypes.string.isRequired,
  lineName: PropTypes.string.isRequired,
};

export default AddVcForm;
